import React, { useState, useMemo } from 'react';
import { Level } from '../../../types';
import { KlSheet } from '../modal/KlSheet';

export interface MobileUnitSelectorSheetProps {
    open: boolean;
    onClose: () => void;
    task: { id: string; code: string; name: string };
    validUnits: Array<{
        id: string;
        name: string;
        levelId: string;
        quantity?: string | number;
        unitPrice?: string;
        value: number;
    }>;
    levels: Level[];
    tempSelectedUnits: string[];
    onToggleUnit: (unitId: string) => void;
    onToggleAll: () => void;
    onConfirm: () => void;
    isUnitAlreadyContracted: (unitId: string, taskId: string) => boolean;
}

export const MobileUnitSelectorSheet: React.FC<MobileUnitSelectorSheetProps> = ({
    open,
    onClose,
    task,
    validUnits,
    levels,
    tempSelectedUnits,
    onToggleUnit,
    onToggleAll,
    onConfirm,
    isUnitAlreadyContracted
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const totalSelected = useMemo(() => {
        return tempSelectedUnits.reduce((acc, uid) => {
            const u = validUnits.find(v => v.id === uid);
            return acc + (u?.value || 0);
        }, 0);
    }, [tempSelectedUnits, validUnits]);

    const allSelected = validUnits.length > 0 && validUnits.every(u => tempSelectedUnits.includes(u.id));

    const filteredUnits = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return validUnits;
        return validUnits.filter(u => {
            const levelName = levels.find(l => l.id === u.levelId)?.name || '';
            return u.name.toLowerCase().includes(q) || levelName.toLowerCase().includes(q);
        });
    }, [validUnits, searchTerm, levels]);

    return (
        <KlSheet
            top={56}
            open={open}
            onClose={onClose}
            title={`Unidades: ${task.name}`}
            footer={
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-[0_0_36%] h-11 rounded-[12px] font-bold text-[14px] border border-[#CBD5E1] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#334155] dark:text-slate-200 active:scale-[0.98] transition-transform"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={validUnits.length === 0}
                        className={`flex-1 h-11 rounded-[12px] font-bold text-[14px] text-white flex items-center justify-center gap-1.5 transition-all active:scale-[0.98] ${
                            validUnits.length === 0
                                ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed'
                                : 'bg-emerald-600 hover:bg-emerald-700 shadow-md'
                        }`}
                    >
                        <span>Guardar</span>
                        <span className="font-mono text-xs opacity-90">(${totalSelected.toLocaleString('es-CL')})</span>
                    </button>
                </div>
            }
        >
            <div className="flex flex-col -mx-3.5 -mt-3.5">
                {/* Cabecera contextual de la partida */}
                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border-b border-[#E2E8F0] dark:border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-[11px] font-semibold px-2 py-0.5 rounded bg-white dark:bg-slate-700 text-[#64748B] dark:text-slate-300 border border-slate-200 dark:border-slate-600 shrink-0">
                            {task.code}
                        </span>
                        <span className="text-[12px] font-bold text-[#0F172A] dark:text-slate-200 truncate">
                            {task.name}
                        </span>
                    </div>
                    <span className="text-[11px] font-mono font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded shrink-0">
                        ${totalSelected.toLocaleString('es-CL')}
                    </span>
                </div>

                {/* Buscador estilo 07 */}
                <div className="p-2.5 px-3 bg-white dark:bg-slate-900 border-b border-[#E2E8F0] dark:border-slate-800">
                    <div className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 flex items-center gap-2">
                        <svg
                            className="w-[18px] h-[18px] text-slate-400 shrink-0"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                        </svg>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar unidad física o nivel…"
                            className="w-full bg-transparent text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="text-slate-400 hover:text-slate-600 p-1"
                            >
                                <span className="material-symbols-outlined text-[16px]">close</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Barra de Seleccionar Todo */}
                {validUnits.length > 0 && (
                    <div
                        onClick={onToggleAll}
                        className="px-3 py-2 bg-[#EFF6FF] dark:bg-blue-950/40 border-b border-blue-100 dark:border-blue-900/40 flex items-center gap-2.5 cursor-pointer select-none"
                    >
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => {}} // controlado por onClick del contenedor
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                        />
                        <span className="text-[12px] font-bold text-blue-700 dark:text-blue-300">
                            {allSelected ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
                        </span>
                        <span className="text-[11px] text-blue-500 dark:text-blue-400 ml-auto font-medium">
                            ({tempSelectedUnits.length}/{validUnits.length} seleccionadas)
                        </span>
                    </div>
                )}

                {/* Lista de filas de 44px estilo 07 */}
                <div className="overflow-y-auto overscroll-contain pb-6">
                    {filteredUnits.length === 0 ? (
                        <div className="p-8 text-center text-xs text-slate-400">
                            No se encontraron unidades físicas asociadas a esta partida en la Matriz EDT.
                        </div>
                    ) : (
                        filteredUnits.map((unit) => {
                            const levelName = levels.find(l => l.id === unit.levelId)?.name || '-';
                            const alreadyInAnotherContract = isUnitAlreadyContracted(unit.id, task.id);
                            const isSelected = tempSelectedUnits.includes(unit.id);

                            return (
                                <div
                                    key={unit.id}
                                    onClick={() => {
                                        if (!alreadyInAnotherContract) {
                                            onToggleUnit(unit.id);
                                        }
                                    }}
                                    className={`flex items-center gap-2.5 min-h-[44px] px-3 py-1.5 border-b border-[#F1F5F9] dark:border-slate-800 transition-colors select-none ${
                                        alreadyInAnotherContract
                                            ? 'opacity-40 bg-slate-50 dark:bg-slate-800/40 cursor-not-allowed'
                                            : isSelected
                                                ? 'bg-[#EFF6FF] dark:bg-blue-950/30 cursor-pointer active:bg-blue-100/50'
                                                : 'bg-white dark:bg-slate-900 cursor-pointer hover:bg-slate-50 active:bg-blue-50/40'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={alreadyInAnotherContract || isSelected}
                                        disabled={alreadyInAnotherContract}
                                        onChange={() => {}} // controlado por onClick del contenedor
                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 shrink-0 pointer-events-none"
                                    />
                                    <div className="flex-1 min-w-0 pr-1">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100 truncate">
                                                {unit.name}
                                            </span>
                                            {alreadyInAnotherContract && (
                                                <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 uppercase">
                                                    Asignado
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] text-[#64748B] dark:text-slate-400 mt-0.5">
                                            <span className="truncate">{levelName}</span>
                                            <span>•</span>
                                            <span>Cant: {unit.quantity ?? 1}</span>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="font-mono text-[13px] font-bold text-blue-700 dark:text-blue-300">
                                            ${unit.value.toLocaleString('es-CL')}
                                        </div>
                                        {unit.unitPrice && (
                                            <div className="font-mono text-[10px] text-slate-400">
                                                ${parseInt(unit.unitPrice).toLocaleString('es-CL')} c/u
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </KlSheet>
    );
};
